// ── session-reassign-modal: reassign session coach (Wave 6, staff-only) ─────
// POST /sessions/:id/reassign-coach {new_coach_id}. Admin/super_admin ONLY — a coach
// cannot reassign a session to another coach (the backend 403s, and the affordance
// is never rendered for coach). The page owns the POST + toast (only after the
// backend confirms). A refused / closed write reads as its honest message inline
// and the dialog stays open.

import { Alert, Button, Input, Modal } from "@vjal/ui";
import {  useState } from "react";

import { asLiveApiId } from "../../hooks/useLiveProductData";

import type { CoachingRecordInput, CoachingSessionDto } from "../../services/coaching-dtos";
import { describeSessionWriteError } from "./sessions-helpers";

export interface SessionReassignModalProps {
  open: boolean;
  session: CoachingSessionDto | null;
  onClose: () => void;
  onConfirm: (body: CoachingRecordInput) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

export function SessionReassignModal({
  open,
  session,
  onClose,
  onConfirm,
  submitting = false,
  submitError
}: SessionReassignModalProps) {
  const [coachId, setCoachId] = useState("");

  // Reset on open transition as a render adjustment (bug-pack class, 2026-07-06).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open && coachId !== "") setCoachId("");
  }

  const topError = describeSessionWriteError(submitError);
  const title = session?.title?.trim() ? session.title : "this session";
  const canSubmit = coachId.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose} title="Reassign coach">
      <div className="flex flex-col gap-4">
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}
        <Alert variant="info">
          Reassigning the coach for {title} transfers future hosting; past sessions and recordings are preserved.
        </Alert>
        <Input
          label="New coach id"
          aria-label="New coach id"
          value={coachId}
          onChange={(event) => setCoachId(event.target.value)}
        />
        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            isLoading={submitting}
            disabled={!canSubmit}
            onClick={() => {
              void onConfirm({ new_coach_id: asLiveApiId(coachId.trim()) }).catch(() => {
                // The owning mutation surfaces the error via submitError.
              });
            }}
          >
            Reassign coach
          </Button>
        </div>
      </div>
    </Modal>
  );
}
